import { ViewableIssue } from 'src/app/account/components/issues-view/issues-view.component'
import {
  AcmCertificateNotFoundIssue,
  AmiStorageNotAccessibleIssue,
  BucketBackfillFailedIssue,
  ClientErrorIssue,
  DeleteFailedIssue,
  DetailedIssue,
  DrsAgentNotFoundIssue,
  EksClusterAccessDeniedIssue,
  EksRequiredEndpointInaccessibleIssue,
  EndpointInaccessibleIssue,
  FailbackResourceIssue,
  InfectedFiles,
  KeyPairNotFoundIssue,
  MalwareScanIssue,
  MarketplaceActionRequiredIssue,
  MarketplaceAmiCantBeCopiedIssue,
  MarketplaceInstanceCantBeCopiedIssue,
  MultipleMatchingImagesIssue,
  NewTargetPlanRequiredIssue,
  PersistentIssue,
  QuotaIssue,
  RaisedIssueScope,
  RefreshClientErrorIssue,
  ResourcePolicyDeniedIssue,
  S3BucketReplicationAlreadyConfiguredIssue,
  S3BucketReplicationNotConfiguredIssue,
  S3RecoveryBucketNameConflictIssue,
  ServiceControlPolicyPermissionIssue,
  SubscriptionOverageIssue,
  SyncPairPermissionIssue,
  UnconfiguredAzureBlobContainerIssue,
  UnconfiguredS3BucketReplicationIssue,
  UnhandledRouteTargetIssue,
  UninstallDrsAgentIssue,
} from 'src/app/account/services/issue'
import { ResourceType } from 'src/utils/resource-types'
import { Arn } from 'src/utils/types'
import { resetIssueCounter, vi } from './showcase-issue-mock-utils'

export function buildAllMockIssues(): ViewableIssue[] {
  resetIssueCounter()
  const issues: ViewableIssue[] = []

  // 1. FailbackResourceIssue (existing)
  const failbackIssue = new FailbackResourceIssue(
    'This message comes from the API and tells you why Arpio could not do the thing.',
    'arn:....' as Arn,
    'resourceId',
    ResourceType.ec2Instance,
    new Date()
  )
  issues.push({
    ...vi(failbackIssue, ResourceType.ec2Instance, RaisedIssueScope.FAILBACK),
    timestamp: failbackIssue.timestamp,
  })

  // 2. UnconfiguredS3BucketReplicationIssue (existing)
  issues.push(
    vi(
      new UnconfiguredS3BucketReplicationIssue(
        'my-data-bucket',
        'Bucket replication has not been configured for this bucket.'
      ),
      ResourceType.s3Bucket,
      RaisedIssueScope.BACKUP,
      'my-data-bucket'
    )
  )

  // 3. S3BucketReplicationNotConfiguredIssue (existing)
  issues.push(
    vi(
      new S3BucketReplicationNotConfiguredIssue(
        'bucketName',
        'arn:aws:iam::123456:role/replication-role',
        'arn:aws:s3:::target-bucket',
        'arn:aws:kms:us-east-1:123456:key/abc-123',
        'Please configure replication for this bucket.'
      ),
      ResourceType.s3Bucket,
      RaisedIssueScope.BACKUP,
      'bucketName'
    )
  )

  // 4. BucketBackfillFailedIssue (existing)
  issues.push(
    vi(
      new BucketBackfillFailedIssue(
        ['s3://bucket/object1', 's3://bucket/object2'],
        100,
        2,
        'Some objects failed to copy.'
      ),
      ResourceType.s3Bucket,
      RaisedIssueScope.BACKUP,
      'backfill-bucket'
    )
  )

  // 5. DrsAgentNotFoundIssue (existing)
  issues.push(
    vi(
      new DrsAgentNotFoundIssue('DRS agent not found for this instance.', 'hw-abc123', false),
      ResourceType.ec2Instance,
      RaisedIssueScope.BACKUP,
      'i-0abc123def'
    )
  )

  // 6. EksClusterAccessDeniedIssue (existing)
  issues.push(
    vi(
      new EksClusterAccessDeniedIssue(
        'Arpio does not have access to EKS Cluster MOCK-TEST-eks-cluster. Please ensure IAM Role arn:aws:iam::000000000000:role/ArpioPrimaryK8sDel-MOCK-TEST-us-east-1 is in the kube-system aws-auth ConfigMap.',
        'MOCK-TEST-eks-cluster',
        'arn:aws:iam::000000000000:role/ArpioPrimaryK8sDel-MOCK-TEST-us-east-1',
        'us-east-1',
        'failure to get dynamic client for cluster_name - 401:Unauthorized'
      ),
      ResourceType.eksCluster,
      RaisedIssueScope.ENHANCE_FOR_RECOVERY_POINT,
      'MOCK-TEST-eks-cluster'
    )
  )

  // 7. EndpointInaccessibleIssue (existing)
  issues.push(
    vi(
      new EndpointInaccessibleIssue(
        'Arpio failed to connect to the AWS API at endpoint "https://s3.us-east-1.amazonaws.com/arpioprimaryaccess-7vgfhhye2-primaryscratchbucket-1b9k059oi3rh1/eks/priv-test/d9c8a6bc-9a5a-4b90-8112-ea5c241d9a69.enc".',
        'https://s3.us-east-1.amazonaws.com/arpioprimaryaccess-7vgfhhye2-primaryscratchbucket-1b9k059oi3rh1/eks/priv-test/d9c8a6bc-9a5a-4b90-8112-ea5c241d9a69.enc'
      ),
      ResourceType.eksCluster,
      RaisedIssueScope.ENHANCE_FOR_RECOVERY_POINT,
      'priv-test'
    )
  )

  // 8. EksRequiredEndpointInaccessibleIssue (existing)
  issues.push(
    vi(
      new EksRequiredEndpointInaccessibleIssue(
        'Arpio failed to connect to the AWS API at endpoint "https://s3.us-east-1.amazonaws.com/...".',
        'https://s3.us-east-1.amazonaws.com/arpioprimaryaccess-7vgfhhye2-primaryscratchbucket-1b9k059oi3rh1/eks/priv-test/d9c8a6bc.enc',
        's3',
        ['subnet-abcde', 'subnet-foobar']
      ),
      ResourceType.eksCluster,
      RaisedIssueScope.ENHANCE_FOR_RECOVERY_POINT,
      'priv-test'
    )
  )

  // 9. DetailedIssue (existing)
  issues.push(
    vi(
      new DetailedIssue(
        "A certificate for 'hotel-chcom-dxapi-prod.dxprod.cloud.chotel.com' could not be found in the recovery environment. Certificates need to be manually provisioned in the recovery environment for Arpio to use.",
        'Detailed Issue Title',
        'Original Error',
        'additional_details_title',
        'additional_details_url',
        true
      ),
      ResourceType.eksCluster,
      RaisedIssueScope.ENHANCE_FOR_RECOVERY_POINT,
      'Detailed Issue 1'
    )
  )

  issues.push(
    vi(
      new DetailedIssue(
        'Please run one of the following commands to fix things.',
        'Detailed Issue Title',
        null,
        null,
        null,
        false,
        {
          bash: 'aws eks update-kubeconfig --name my-cluster --region us-west-2\nkubectl apply -f https://raw.githubusercontent.com/arpio/arpio-agent/main/deploy.yaml\nkubectl get pods -n arpio-system',
          powershell:
            'aws eks update-kubeconfig --name my-cluster --region us-west-2\nkubectl apply -f https://raw.githubusercontent.com/arpio/arpio-agent/main/deploy.yaml\nGet-Process | Where-Object { $_.Name -eq "arpio-agent" }',
        }
      ),
      ResourceType.eksCluster,
      RaisedIssueScope.ENHANCE_FOR_RECOVERY_POINT,
      'Detailed Issue 2'
    )
  )

  // 9b. DetailedIssue — backend-driven EKS onboarding scripts, one mock per auth mode.
  // The two API-mode clusters share a message shape, so dedup merges them into one
  // sub-group (one script shown, Affected Resources (2), "Showing error for X").
  // Legacy eksClusterAccessDenied mock (#6) proves the old fallback still renders.
  const eksRole = 'arn:aws:iam::000000000000:role/ArpioPrimaryK8sDel-MOCK-TEST-us-east-1'
  const eksApiScript = (cluster: string) =>
    `aws eks create-access-entry --region=us-east-1 --cluster-name ${cluster} ` +
    `--principal-arn ${eksRole} --type STANDARD --user Arpio ; ` +
    `aws eks associate-access-policy --region=us-east-1 --cluster-name ${cluster} ` +
    `--principal-arn ${eksRole} --policy-arn arn:aws:eks::aws:cluster-access-policy/AmazonEKSAdminViewPolicy ` +
    `--access-scope type=cluster`
  const eksConfigMapScript = (cluster: string) =>
    `eksctl create iamidentitymapping --cluster ${cluster} --region=us-east-1 ` +
    `--arn ${eksRole} --username Arpio --group system:masters --no-duplicate-arns`
  // resourceName must be the cluster name embedded in the message/scripts, or dedup can't strip it.
  const eksAccessIssue = (cluster: string, instruction: string, scripts: { [language: string]: string }) =>
    vi(
      new DetailedIssue(
        `Arpio does not have access to EKS Cluster ${cluster}. ${instruction}`,
        'Insufficient EKS cluster permissions',
        'failure to get dynamic client for cluster_name - 401:Unauthorized',
        'Setting up EKS documentation',
        'http://docs.arpio.io/setting-up-eks',
        false,
        scripts
      ),
      ResourceType.eksCluster,
      RaisedIssueScope.ENHANCE_FOR_RECOVERY_POINT,
      cluster
    )
  const eksApiInstruction = `Run the following command to create an EKS access entry for IAM Role ${eksRole}.`
  for (const cluster of ['MOCK-TEST-eks-cluster', 'MOCK-TEST-eks-cluster-2']) {
    issues.push(eksAccessIssue(cluster, eksApiInstruction, { 'EKS API': eksApiScript(cluster) }))
  }
  issues.push(
    eksAccessIssue(
      'MOCK-TEST-eks-cluster-configmap',
      `Run the following command to add IAM Role ${eksRole} to the kube-system aws-auth ConfigMap.`,
      { 'aws-auth ConfigMap (deprecated)': eksConfigMapScript('MOCK-TEST-eks-cluster-configmap') }
    )
  )
  issues.push(
    eksAccessIssue(
      'MOCK-TEST-eks-cluster-dual',
      `Grant access for IAM Role ${eksRole} with one of the following command sets.`,
      {
        'EKS API': eksApiScript('MOCK-TEST-eks-cluster-dual'),
        'aws-auth ConfigMap (deprecated)': eksConfigMapScript('MOCK-TEST-eks-cluster-dual'),
      }
    )
  )

  // --- New issue types below ---

  // 10. PersistentIssue
  issues.push(
    vi(
      new PersistentIssue('This is a persistent notification that remains visible until manually dismissed.'),
      ResourceType.lambdaFunction,
      RaisedIssueScope.BACKUP,
      'my-lambda-function'
    )
  )

  // 11. DeleteFailedIssue
  issues.push(vi(new DeleteFailedIssue(), ResourceType.ec2Instance, RaisedIssueScope.CLEANUP, 'i-0delete123'))

  // 12. AcmCertificateNotFoundIssue
  issues.push(
    vi(
      new AcmCertificateNotFoundIssue(
        "A certificate for 'api.example.com' could not be found in the recovery environment.",
        'arn:aws:acm:us-east-1:123456:certificate/abc-123',
        'api.example.com'
      ),
      ResourceType.acmCertificate,
      RaisedIssueScope.RESTORE,
      'api.example.com'
    )
  )

  // 13. ClientErrorIssue
  issues.push(
    vi(
      new ClientErrorIssue(
        'An error occurred while modifying the DB instance.',
        'InvalidParameterCombination',
        'The DB instance class db.t3.micro does not support encryption.',
        'ModifyDBInstance'
      ),
      ResourceType.rdsDbInstance,
      RaisedIssueScope.RESTORE,
      'my-database'
    )
  )

  // 14. QuotaIssue
  issues.push(
    vi(
      new QuotaIssue(
        'You have exceeded the maximum number of EC2 instances allowed in this region.',
        'InstanceLimitExceeded',
        'Your quota allows for 0 more running instance(s).',
        'RunInstances'
      ),
      ResourceType.ec2Instance,
      RaisedIssueScope.RESTORE,
      'i-0quota456'
    )
  )

  // 15. MultipleMatchingImagesIssue
  const matchCriteria = new Map<string, Array<string>>()
  matchCriteria.set('name', ['my-ami-*'])
  matchCriteria.set('tag:Environment', ['production'])
  issues.push(
    vi(
      new MultipleMatchingImagesIssue(
        'Multiple AMIs match the search criteria. Please ensure only one AMI matches.',
        matchCriteria
      ),
      ResourceType.ec2Image,
      RaisedIssueScope.SCAN,
      'ami-0multi123'
    )
  )

  // 16. MarketplaceAmiCantBeCopiedIssue
  const amiMatchCriteria = new Map<string, Array<string>>()
  amiMatchCriteria.set('name', ['marketplace-ami-*'])
  issues.push(
    vi(
      new MarketplaceAmiCantBeCopiedIssue(
        'This AMI is an AWS Marketplace product and cannot be copied across accounts.',
        ['prod-abc123'],
        amiMatchCriteria
      ),
      ResourceType.ec2Image,
      RaisedIssueScope.RESTORE,
      'ami-marketplace-01'
    )
  )

  // 17. MarketplaceInstanceCantBeCopiedIssue
  issues.push(
    vi(
      new MarketplaceInstanceCantBeCopiedIssue(
        'This instance uses an AWS Marketplace AMI and cannot be directly copied.'
      ),
      ResourceType.ec2Instance,
      RaisedIssueScope.RESTORE,
      'i-0marketplace789'
    )
  )

  // 18. AmiStorageNotAccessibleIssue
  const storageMatchCriteria = new Map<string, Array<string>>()
  storageMatchCriteria.set('name', ['encrypted-ami-*'])
  issues.push(
    vi(
      new AmiStorageNotAccessibleIssue(
        'The AMI storage is encrypted with a key that is not accessible in the recovery region.',
        storageMatchCriteria
      ),
      ResourceType.ec2Image,
      RaisedIssueScope.RESTORE,
      'ami-encrypted-01'
    )
  )

  // 19. UnhandledRouteTargetIssue
  issues.push(
    vi(
      new UnhandledRouteTargetIssue(
        'Route table contains a route target that Arpio does not know how to handle.',
        'arn:aws:ec2:us-east-1:123456:route-table/rtb-abc123',
        'vpce-svc-abc123'
      ),
      ResourceType.ec2RouteTable,
      RaisedIssueScope.BACKUP,
      'rtb-abc123'
    )
  )

  // 20. MalwareScanIssue (infected)
  issues.push(
    vi(
      new MalwareScanIssue(
        'infected',
        'scan-abc123',
        'Malware was detected during a scan of the EBS volume.',
        'https://console.aws.amazon.com/guardduty',
        [new InfectedFiles('Trojan.GenericKD', 3, ['/var/log/malware.exe', '/tmp/payload.bin', '/opt/bad.sh'])]
      ),
      ResourceType.ecsService,
      RaisedIssueScope.RESTORE,
      'my-ecs-service'
    )
  )

  // 21. MalwareScanIssue (failed)
  issues.push(
    vi(
      new MalwareScanIssue(
        'failed',
        'scan-def456',
        'The malware scan could not be completed due to an internal error.',
        'https://console.aws.amazon.com/guardduty',
        []
      ),
      ResourceType.ec2Instance,
      RaisedIssueScope.RESTORE,
      'i-0scanfail789'
    )
  )

  // 22. MarketplaceActionRequiredIssue
  issues.push(
    vi(
      new MarketplaceActionRequiredIssue(
        'The recovery account must subscribe to this AWS Marketplace product before recovery can proceed.',
        'https://aws.amazon.com/marketplace/pp/prodview-abc123'
      ),
      ResourceType.ec2Instance,
      RaisedIssueScope.RESTORE,
      'i-0marketplace-action'
    )
  )

  // 23. RefreshClientErrorIssue
  issues.push(
    vi(
      new RefreshClientErrorIssue(
        'An error occurred while refreshing the DynamoDB table.',
        'ResourceNotFoundException',
        'Requested resource not found: Table: my-table not found',
        'DescribeTable',
        'dynamoDbTable'
      ),
      ResourceType.dynamoDbTable,
      RaisedIssueScope.SCAN,
      'my-dynamo-table'
    )
  )

  // 24. KeyPairNotFoundIssue
  issues.push(
    vi(new KeyPairNotFoundIssue('my-ec2-keypair'), ResourceType.ec2Instance, RaisedIssueScope.BACKUP, 'i-0keypair123')
  )

  // 25. S3BucketReplicationAlreadyConfiguredIssue
  issues.push(
    vi(
      new S3BucketReplicationAlreadyConfiguredIssue('already-replicated-bucket'),
      ResourceType.s3Bucket,
      RaisedIssueScope.BACKUP,
      'already-replicated-bucket'
    )
  )

  // 26. S3RecoveryBucketNameConflictIssue
  issues.push(
    vi(
      new S3RecoveryBucketNameConflictIssue(
        'primary-data-bucket',
        'primary-data-bucket-recovery',
        'The recovery bucket name conflicts with an existing bucket in the recovery account.'
      ),
      ResourceType.s3Bucket,
      RaisedIssueScope.BACKUP,
      'primary-data-bucket'
    )
  )

  // 27. UninstallDrsAgentIssue
  issues.push(
    vi(
      new UninstallDrsAgentIssue('s-abc123def', 'i-0drsuninstall'),
      ResourceType.ec2Instance,
      RaisedIssueScope.CLEANUP,
      'i-0drsuninstall'
    )
  )

  // 28. SyncPairPermissionIssue (general - no IAM operation)
  // Note: The dedicated sub-component requires a working syncPair to render its update-access dialog.
  // It still renders the accordion header + message via the dedicated component.
  issues.push(
    vi(
      new SyncPairPermissionIssue(
        'Arpio does not have permission to describe load balancers in the recovery account. Please update access templates.',
        'DescribeLoadBalancers'
      ),
      ResourceType.elbLoadBalancer,
      RaisedIssueScope.RESTORE,
      'my-load-balancer'
    )
  )

  // 29. SyncPairPermissionIssue (IAM operation - triggers IAM variant)
  // Note: The iam-permission-issue sub-component subscribes to syncPair.manualResourceTemplateUrl$ in ngOnInit.
  // With a fake syncPair this may error but should not prevent rendering.
  issues.push(
    vi(
      new SyncPairPermissionIssue(
        'Arpio does not have permission to create IAM roles in the recovery account. Please grant the CreateRole permission.',
        'CreateRole'
      ),
      ResourceType.iamRole,
      RaisedIssueScope.RESTORE,
      'arpio-recovery-role'
    )
  )

  // 30. ResourcePolicyDeniedIssue
  issues.push(
    vi(
      new ResourcePolicyDeniedIssue(
        'Access to this secret is explicitly denied by a resource-based policy. Please update the policy to allow Arpio access.'
      ),
      ResourceType.secretsManagerSecret,
      RaisedIssueScope.RESTORE,
      'prod/database/credentials'
    )
  )

  // 31. ServiceControlPolicyPermissionIssue
  issues.push(
    vi(
      new ServiceControlPolicyPermissionIssue(
        'Access to this KMS key is denied by a Service Control Policy in your AWS Organization. Please update the SCP to allow this operation.'
      ),
      ResourceType.kmsKey,
      RaisedIssueScope.RESTORE,
      'alias/my-encryption-key'
    )
  )

  // 32. SubscriptionOverageIssue
  issues.push(
    vi(
      new SubscriptionOverageIssue(
        'Your current subscription allows protection of up to 50 resources. You are currently protecting 63 resources. Please contact sales to increase your subscription.'
      ),
      ResourceType.ec2Instance,
      RaisedIssueScope.BACKUP,
      'subscription-limit'
    )
  )

  // 33. NewTargetPlanRequiredIssue
  issues.push(
    vi(
      new NewTargetPlanRequiredIssue(
        'Availability zones have changed in the recovery region. A new subnet-AZ mapping is required.',
        [
          { srcSubnetId: 'subnet-src1', tgtSubnetId: 'subnet-tgt1', oldAz: 'us-west-2a', newAz: 'us-west-2b' },
          { srcSubnetId: 'subnet-src2', tgtSubnetId: 'subnet-tgt2', oldAz: 'us-west-2b', newAz: 'us-west-2c' },
        ]
      ),
      ResourceType.ec2Subnet,
      RaisedIssueScope.RESTORE,
      'subnet-src1'
    )
  )

  // 34. UnconfiguredAzureBlobContainerIssue
  // Note: The dedicated sub-component calls syncPair/application.getSyncPairResourceConfig$ in ngOnInit.
  // With a fake syncPair this may produce an API error but the form should still render.
  issues.push(
    vi(
      new UnconfiguredAzureBlobContainerIssue(
        'mystorageaccount',
        'mycontainer',
        'Blob container replication has not been configured. Please configure replication settings.'
      ),
      ResourceType.azureBlobContainer,
      RaisedIssueScope.BACKUP,
      'mystorageaccount/mycontainer'
    )
  )

  return issues
}
